import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import {
  KeyRound,
  MoreHorizontal,
  Plus,
  ShieldCheck,
  ShieldQuestion,
  SquarePen,
  Users,
} from 'lucide-react';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Input } from '@/components/ui/input';
import { MasterTable, type MasterTableColumn } from '@/components/efs/MasterTable';
import { PageHeader } from '@/components/efs/PageHeader';
import { TableToolbar } from '@/components/efs/TableToolbar';
import { useStartPermissionSimulation } from '@/features/auth/useAuth';
import { useCurrentUser } from '@/lib/auth-context';
import { homeRouteFor } from '@/lib/role-access';

import { UserFormDialog } from './UserFormDialog';
import { UserPasswordDialog } from './UserPasswordDialog';
import { UserPermissionsDialog } from './UserPermissionsDialog';
import { useUpdateUser, useUsersList } from './useUsers';
import type { EfsUser } from './types';

const NS = 'settings';

export const UsersPage = () => {
  const { t } = useTranslation(NS);
  const navigate = useNavigate();
  const me = useCurrentUser();
  const [search, setSearch] = useState('');
  const [showInactive, setShowInactive] = useState(false);
  const [createOpen, setCreateOpen] = useState(false);
  const [editRow, setEditRow] = useState<EfsUser | null>(null);
  const [permissionsRow, setPermissionsRow] = useState<EfsUser | null>(null);
  const [passwordRow, setPasswordRow] = useState<EfsUser | null>(null);

  // Without this, deactivated users vanish from the list — and with them the
  // "Activate" action — making deactivation effectively one-way.
  const query = useUsersList({ q: search || undefined, active_only: !showInactive });
  const update = useUpdateUser();
  const startSimulation = useStartPermissionSimulation();

  const toggleActive = async (row: EfsUser) => {
    await update.mutateAsync({
      id: row.user_id,
      body: {
        display_name: row.display_name,
        active_flag: !row.active_flag,
      },
    });
  };

  const simulateUser = async (row: EfsUser) => {
    const simulatedUser = await startSimulation.mutateAsync({ userId: row.user_id });
    navigate(homeRouteFor(simulatedUser), { replace: true });
  };

  const columns = useMemo<MasterTableColumn<EfsUser>[]>(
    () => [
      {
        key: 'email',
        header: t('efs.settings.users.fields.email'),
        cell: (row) => <span className="font-medium">{row.email}</span>,
      },
      {
        key: 'display_name',
        header: t('efs.settings.users.fields.display_name'),
        cell: (row) => row.display_name,
      },
      {
        key: 'company',
        header: t('efs.settings.users.fields.company'),
        cell: (row) => row.company_display_name ?? '—',
      },
      {
        key: 'facility',
        header: t('efs.settings.users.fields.facility'),
        cell: (row) => row.facility_name ?? '—',
      },
      {
        key: 'permissions',
        header: t('efs.settings.users.fields.permissions'),
        cell: (row) => (
          <div className="flex flex-wrap items-center gap-1">
            <span className="inline-flex items-center rounded-md border border-efs-border bg-efs-bg-subtle px-1.5 py-0.5 text-xs">
              {t('efs.settings.users.permissions_count', { count: row.permissions.length })}
            </span>
            {row.roles.includes('system_admin') ? (
              <Badge variant="secondary">{t('efs.settings.users.roles.system_admin')}</Badge>
            ) : null}
          </div>
        ),
      },
      {
        key: 'status',
        header: t('efs.settings.users.fields.status'),
        cell: (row) =>
          row.active_flag ? (
            <Badge variant="default">{t('efs.settings.users.fields.active')}</Badge>
          ) : (
            <Badge variant="secondary">{t('efs.settings.users.fields.inactive')}</Badge>
          ),
      },
      {
        key: 'actions',
        header: '',
        align: 'right',
        cell: (row) => {
          const isSelf = me?.id === row.user_id;
          return (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon"
                  aria-label={t('efs.common.actions.menu', { defaultValue: 'メニュー' })}
                >
                  <MoreHorizontal className="h-4 w-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem onSelect={() => setEditRow(row)}>
                  <SquarePen className="mr-2 h-4 w-4" />
                  {t('efs.settings.users.actions.edit')}
                </DropdownMenuItem>
                <DropdownMenuItem onSelect={() => setPermissionsRow(row)}>
                  <ShieldCheck className="mr-2 h-4 w-4" />
                  {t('efs.settings.users.actions.set_roles')}
                </DropdownMenuItem>
                <DropdownMenuItem onSelect={() => setPasswordRow(row)}>
                  <KeyRound className="mr-2 h-4 w-4" />
                  {t('efs.settings.users.actions.reset_password')}
                </DropdownMenuItem>
                <DropdownMenuItem
                  disabled={isSelf || !row.active_flag || startSimulation.isPending}
                  onSelect={() => void simulateUser(row)}
                >
                  <ShieldQuestion className="mr-2 h-4 w-4" />
                  {t('efs.settings.users.actions.simulate_permissions')}
                </DropdownMenuItem>
                <DropdownMenuItem
                  disabled={isSelf && row.active_flag}
                  onSelect={() => void toggleActive(row)}
                >
                  {row.active_flag
                    ? t('efs.settings.users.actions.deactivate')
                    : t('efs.settings.users.actions.activate')}
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          );
        },
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [t, me?.id],
  );

  const rows = query.data ?? [];

  return (
    <div className="space-y-4">
      <PageHeader
        title={t('efs.settings.users.title')}
        subtitle={t('efs.settings.users.subtitle')}
        count={rows.length}
        actions={
          <Button onClick={() => setCreateOpen(true)}>
            <Plus className="h-4 w-4" />
            {t('efs.settings.users.actions.create')}
          </Button>
        }
      />

      <div className="min-w-0 space-y-3 rounded-md border border-efs-border bg-efs-bg-panel p-3">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <div className="flex items-center gap-2 text-sm font-semibold text-efs-text-primary">
              <Users className="h-4 w-4" />
              {t('efs.settings.users.sections.staff')}
            </div>
            <p className="mt-1 text-xs text-efs-text-muted">
              {t('efs.settings.users.sections.staff_hint')}
            </p>
          </div>
          <div className="flex gap-2 text-xs text-efs-text-muted">
            <span className="rounded-md border border-efs-border px-2 py-1">
              {t('efs.settings.users.summary.staff', { count: rows.length })}
            </span>
          </div>
        </div>

        <TableToolbar
          count={t('efs.common.messages.count', {
            defaultValue: '{{count}} 件',
            count: rows.length,
          })}
          filters={
            <div className="flex items-center gap-3">
              <Input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder={t('efs.settings.users.search_placeholder', {
                  defaultValue: 'メールで検索',
                })}
                className="h-8 w-64"
              />
              <label className="flex items-center gap-1.5 text-xs text-efs-text-muted">
                <input
                  type="checkbox"
                  className="h-3.5 w-3.5"
                  checked={showInactive}
                  onChange={(e) => setShowInactive(e.target.checked)}
                />
                {t('efs.settings.users.show_inactive')}
              </label>
            </div>
          }
        />

        <MasterTable
          columns={columns}
          rows={rows}
          rowKey={(row) => row.user_id}
          isLoading={query.isLoading}
        />
      </div>

      <UserFormDialog open={createOpen} onOpenChange={setCreateOpen} />

      {editRow ? (
        <UserFormDialog
          open={!!editRow}
          onOpenChange={(open) => {
            if (!open) setEditRow(null);
          }}
          row={editRow}
        />
      ) : null}

      {permissionsRow ? (
        <UserPermissionsDialog
          open={!!permissionsRow}
          onOpenChange={(open) => {
            if (!open) setPermissionsRow(null);
          }}
          user={
            // Keep the ticked set in sync with the latest list-query payload
            // after the replace mutation invalidates the cache.
            rows.find((r) => r.user_id === permissionsRow.user_id) ?? permissionsRow
          }
        />
      ) : null}

      {passwordRow ? (
        <UserPasswordDialog
          open={!!passwordRow}
          onOpenChange={(open) => {
            if (!open) setPasswordRow(null);
          }}
          user={passwordRow}
        />
      ) : null}
    </div>
  );
};

export default UsersPage;
